/**
 * Forgot Password — request a reset link.
 *
 * Always shows the "check your inbox" message regardless of whether the email
 * exists in our DB (anti-enumeration).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, ArrowRight } from "lucide-react";

import { apiClient } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
    } catch {
      /* deliberately ignore */
    } finally {
      setBusy(false);
      setDone(true);
    }
  };

  if (done) {
    return (
      <main
        className="border-b border-black min-h-[80vh]"
        data-testid="forgot-done"
      >
        <section className="max-w-md mx-auto px-6 lg:px-8 py-16 text-center">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / Check your inbox
          </div>
          <h1 className="font-display text-4xl tracking-tight leading-[1.05] mb-4">
            Reset link sent.
          </h1>
          <p className="text-[#475569] text-sm mb-6 leading-relaxed">
            If an account exists for <code className="font-mono text-black">{email}</code>,
            we&apos;ve emailed a one-hour reset link. Check your inbox (and
            spam) for an email from PrivacyLens.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-black bg-white text-sm hover:bg-[#F1F5F9]"
          >
            Back to login <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main
      className="border-b border-black min-h-[80vh]"
      data-testid="forgot-page"
    >
      <section className="max-w-md mx-auto px-6 lg:px-8 py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
          / Forgot password
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-2">
          Reset your password.
        </h1>
        <p className="text-[#475569] text-sm mb-8">
          We&apos;ll email you a link to choose a new password. The link
          expires in 1 hour.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Email
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Mail className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="you@company.com"
                data-testid="forgot-email-input"
                autoComplete="email"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#0A0A0A] text-white py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#0047FF] transition-colors"
            data-testid="forgot-submit-button"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Sending…" : "Send reset link"}
          </button>

          <div className="text-xs pt-1 text-[#475569]">
            Remembered it?{" "}
            <Link to="/login" className="underline hover:text-[#0047FF]">
              Back to login
            </Link>
          </div>
        </form>
      </section>
    </main>
  );
}
